import { useEffect, useState } from "react";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Target, TrendingUp, Zap, Pencil } from "lucide-react";
import GaugeComponent from "react-gauge-component";
import { Button } from "@/components/primitives";
import { ReflectionGoalDialog } from "@/components/ReflectionGoalDialog";
import Confetti from "react-confetti-boom";
import {
	calculateReflectionProgress,
	type ReflectionGoal,
	type ReflectionProgress,
} from "@/lib/reflection-progress";

interface ReflectionProgressMeterProps {
	userId: string;
	goal: ReflectionGoal | null | undefined;
	onRefresh?: boolean;
	compact?: boolean;
	dayStreak?: number;
	celebrateOnSmashing?: boolean;
}

export function ReflectionProgressMeter({
	userId,
	goal,
	onRefresh,
	compact = false,
	dayStreak,
	celebrateOnSmashing = false,
}: ReflectionProgressMeterProps) {
	const [progress, setProgress] = useState<ReflectionProgress | null>(null);
	const [loading, setLoading] = useState(true);
	const [localGoal, setLocalGoal] = useState<ReflectionGoal | null | undefined>(
		goal,
	);
	const [editingGoal, setEditingGoal] = useState(false);
	const [showConfetti, setShowConfetti] = useState(false);

	useEffect(() => {
		if (!localGoal) {
			setLoading(false);
			return;
		}

		const fetchProgress = async () => {
			setLoading(true);
			const result = await calculateReflectionProgress(userId, localGoal);
			setProgress(result);
			setLoading(false);
		};

		fetchProgress();
	}, [userId, localGoal, onRefresh]);

	// keep local goal in sync if parent changes
	useEffect(() => {
		setLocalGoal(goal);
	}, [goal]);

	// One-time celebration when smashing it (controlled by prop)
	useEffect(() => {
		if (celebrateOnSmashing && progress?.status === "smashing-it") {
			setShowConfetti(true);
			const t = setTimeout(() => setShowConfetti(false), 2200);
			return () => clearTimeout(t);
		}
	}, [celebrateOnSmashing, progress?.status]);

	// One-time shake animation on home (non-compact) when on-target or smashing-it
	const [animateBadge, setAnimateBadge] = useState(false);
	useEffect(() => {
		if (compact) return; // Home uses non-compact
		if (!progress) return;
		const good =
			progress.status === "on-target" || progress.status === "smashing-it";
		const key = "home.performance.badge.shaken";
		if (good) {
			try {
				const done = sessionStorage.getItem(key);
				if (!done) {
					setAnimateBadge(true);
					sessionStorage.setItem(key, "1");
					const t = setTimeout(() => setAnimateBadge(false), 700);
					return () => clearTimeout(t);
				}
			} catch {}
		}
	}, [progress?.status, compact]);

	if (!goal || loading) {
		return null;
	}

	if (!progress) {
		return null;
	}

	const getStatusColor = () => {
		switch (progress.status) {
			case "behind":
				return "text-gray-600 dark:text-gray-400";
			case "on-target":
				return "text-green-600 dark:text-green-400";
			case "smashing-it":
				return "text-green-600 dark:text-green-400";
		}
	};

	const getStatusIcon = () => {
		switch (progress.status) {
			case "behind":
				return <Target className="h-4 w-4" />;
			case "on-target":
				return <TrendingUp className="h-4 w-4" />;
			case "smashing-it":
				return <Zap className="h-4 w-4" />;
		}
	};

	const getStatusText = () => {
		switch (progress.status) {
			case "behind":
				return "Keep going";
			case "on-target":
				return "Great, on target!";
			case "smashing-it":
				return "Smashing it!";
		}
	};

	const getStatusBadgeVariant = () => {
		switch (progress.status) {
			case "behind":
				return "outline";
			case "on-target":
				return "default";
			case "smashing-it":
				return "secondary";
		}
	};

	// Calculate gauge value (align zones with status thresholds)
	const getGaugeValue = () => {
		const maxValue = 150;
		const greenStart = 0.7 * maxValue; // start of green zone on gauge
		const onTargetStart = greenStart;
		const onTargetEnd = 100; // fill through green up to 100

		// Weekly goal min/max (per week), derived from goal
		const getWeeklyRange = (g: ReflectionGoal) => {
			switch (g) {
				case "1-2":
					return { min: 1, max: 2 };
				case "3-5":
					return { min: 3, max: 5 };
				case "6+":
					return { min: 6, max: 7 }; // cap weekly max at 7
			}
			return { min: 1, max: 2 };
		};

		const { min, max } = getWeeklyRange(goal as ReflectionGoal);
		const avg = progress.weeklyAverage;

		if (avg <= 0) return 0;

		if (avg >= 7) return maxValue;

		if (avg < min) {
			// Below target: map 0..min -> 0..(greenStart - epsilon)
			const ratio = Math.max(0, Math.min(1, avg / min));
			return ratio * greenStart;
		}

		if (avg <= max) {
			// On target: map min..max -> (greenStart + epsilon)..(greenStart + small)
			const span = Math.max(0.0001, max - min);
			const ratio = (avg - min) / span;
			return onTargetStart + ratio * (onTargetEnd - onTargetStart);
		}

		// Smashing it: pin to maximum
		return maxValue;
	};

	const gaugeValue = getGaugeValue();

	// Get gauge arc colors based on status
	const getGaugeColors = () => {
		return [
			{ color: "#ff7d7dff", length: 0.33 }, // Softer red (red-300)
			{ color: "#ffdf60ff", length: 0.34 }, // Softer amber (amber-200)
			{ color: "#6ae497ff", length: 0.33 }, // Softer green (green-300)
		];
	};

	if (compact) {
		return (
			<div className="space-y-2 text-left">
				<div className="flex items-center justify-between mb-2">
					<div className="flex items-center gap-2">
						<div className={getStatusColor()}>{getStatusIcon()}</div>
						<span className="text-sm font-medium">{getStatusText()}</span>
					</div>
					<span className="text-sm text-muted-foreground">
						Avg: {Math.ceil(progress.weeklyAverage)}/week
					</span>
				</div>
				<div className="flex justify-center">
					<GaugeComponent
						type="semicircle"
						arc={{
							width: 0.15,
							padding: 0.005,
							cornerRadius: 7,
							subArcs: getGaugeColors(),
						}}
						pointer={{
							type: "blob",
							animationDelay: 100,
						}}
						value={gaugeValue}
						minValue={0}
						maxValue={150}
						style={{ width: "140px" }}
					/>
				</div>
			</div>
		);
	}

	const goalLabel = (g: ReflectionGoal | null | undefined) => {
		switch (g) {
			case "1-2":
				return "1–2 a week";
			case "3-5":
				return "3–5 a week";
			case "6+":
				return "6+ a week";
			default:
				return "Not set";
		}
	};

	return (
		<Card>
			<CardHeader className="p-4 pb-3">
				<div className="flex items-center justify-between">
					<CardTitle className="text-base font-medium">
						Check-in performance
					</CardTitle>
					<Badge
						variant={getStatusBadgeVariant()}
						className={`flex items-center gap-1 ${
							progress.status === "smashing-it" ||
							progress.status === "on-target"
								? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 border-green-200 dark:border-green-800"
								: ""
						} ${animateBadge ? "animate-shake" : ""}`}
					>
						{getStatusIcon()}
						{getStatusText()}
					</Badge>
				</div>
			</CardHeader>
			<CardContent className="p-4 pt-0 space-y-3 text-left">
				{showConfetti && <Confetti mode="boom" particleCount={120} />}
				<div className="flex items-center gap-3">
					<GaugeComponent
						type="semicircle"
						arc={{
							width: 0.16,
							padding: 0.005,
							cornerRadius: 7,
							subArcs: getGaugeColors(),
						}}
						pointer={{
							type: "blob",
							animationDelay: 100,
						}}
						value={gaugeValue}
						minValue={0}
						maxValue={150}
						style={{ width: "130px" }}
						labels={{
							valueLabel: {
								hide: true as any,
							},
							tickLabels: {
								type: "inner" as any,
								hideMinMax: true as any,
								ticks: [] as any,
							},
						}}
					/>
					<div className="flex-1">
						<div className="text-base font-medium text-foreground">
							{Math.ceil(progress.weeklyAverage)} in 7 days
						</div>
						<div className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
							<span>Goal: {goalLabel(localGoal)}</span>
							<Button
								variant="ghost"
								size="icon"
								aria-label="Edit goal"
								className="h-6 w-6"
								onClick={() => setEditingGoal(true)}
							>
								<Pencil className="h-3.5 w-3.5" />
							</Button>
						</div>
					</div>
				</div>
			</CardContent>
			{editingGoal && (
				<ReflectionGoalDialog
					open={editingGoal}
					onGoalSet={(g) => {
						setLocalGoal(g);
						setEditingGoal(false);
					}}
					onSkip={() => setEditingGoal(false)}
					userId={userId}
				/>
			)}
		</Card>
	);
}
